import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminApi, Retailer, RetailerIndustry } from '../../api/client';
import { SkeletonTable } from '../../components/SkeletonTable';
import { InfoTooltip } from '../../components/InfoTooltip';
import { RetailerSetupWizard } from '../../components/wizards/RetailerSetupWizard';

const INDUSTRY_COLORS: Record<RetailerIndustry, string> = {
  'retail': 'bg-blue-100 text-blue-800',
  'grocery': 'bg-green-100 text-green-800',
  'automotive': 'bg-orange-100 text-orange-800',
  'electronics': 'bg-purple-100 text-purple-800',
  'apparel': 'bg-pink-100 text-pink-800',
  'home-goods': 'bg-yellow-100 text-yellow-800',
  'convenience': 'bg-teal-100 text-teal-800',
  'travel-center': 'bg-indigo-100 text-indigo-800',
  'warehouse': 'bg-gray-100 text-gray-800',
  'other': 'bg-slate-100 text-slate-800',
};

export function AdminRetailersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedIndustry, setSelectedIndustry] = useState<RetailerIndustry | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<'active' | 'inactive' | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState<Retailer | null>(null);

  const { data: industriesData } = useQuery({
    queryKey: ['admin', 'industries'],
    queryFn: () => adminApi.getIndustries(),
  });

  const { data: retailersData, isLoading } = useQuery({
    queryKey: ['admin', 'retailers', selectedIndustry, selectedStatus, searchQuery],
    queryFn: () => adminApi.getRetailers({
      industry: selectedIndustry || undefined,
      status: selectedStatus || undefined,
      search: searchQuery || undefined,
    }),
  });

  const createRetailerMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.createRetailer>[0]) => adminApi.createRetailer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'retailers'] });
      setShowCreateModal(false);
    },
  });

  const updateRetailerMutation = useMutation({
    mutationFn: ({ retailerId, data }: { retailerId: string; data: Parameters<typeof adminApi.updateRetailer>[1] }) =>
      adminApi.updateRetailer(retailerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'retailers'] });
      setShowEditModal(false);
      setSelectedRetailer(null);
    },
  });

  const deleteRetailerMutation = useMutation({
    mutationFn: (retailerId: string) => adminApi.deleteRetailer(retailerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'retailers'] });
      setSelectedRetailer(null);
    },
  });

  const handleCreateRetailer = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createRetailerMutation.mutate({
      retailerId: formData.get('retailerId') as string,
      name: formData.get('name') as string,
      industry: formData.get('industry') as RetailerIndustry,
      ediPartnerId: formData.get('ediPartnerId') as string || undefined,
      website: formData.get('website') as string || undefined,
      contactEmail: formData.get('contactEmail') as string || undefined,
      notes: formData.get('notes') as string || undefined,
    });
  };

  const handleUpdateRetailer = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedRetailer) return;
    const formData = new FormData(e.currentTarget);
    updateRetailerMutation.mutate({
      retailerId: selectedRetailer.retailerId,
      data: {
        name: formData.get('name') as string,
        industry: formData.get('industry') as RetailerIndustry,
        status: formData.get('status') as 'active' | 'inactive',
        ediPartnerId: formData.get('ediPartnerId') as string || undefined,
        website: formData.get('website') as string || undefined,
        contactEmail: formData.get('contactEmail') as string || undefined,
        notes: formData.get('notes') as string || undefined,
      },
    });
  };

  const industries = industriesData?.industries || [];
  const retailers = retailersData?.retailers || [];

  // Group retailers by industry for display
  const retailersByIndustry = retailers.reduce((acc, retailer) => {
    if (!acc[retailer.industry]) {
      acc[retailer.industry] = [];
    }
    acc[retailer.industry].push(retailer);
    return acc;
  }, {} as Record<RetailerIndustry, Retailer[]>);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-36 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="card"><SkeletonTable rows={6} columns={5} /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Retailers</h1>
        <div className="flex gap-2">
          <button
            className="btn btn-secondary text-sm"
            onClick={() => setShowSetupWizard(true)}
          >
            🧙 Guided Setup
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Add Retailer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="label">Industry</label>
            <select
              className="input w-48"
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value as RetailerIndustry | '')}
            >
              <option value="">All Industries</option>
              {industries.map((industry) => (
                <option key={industry.id} value={industry.id}>
                  {industry.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select
              className="input w-36"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as 'active' | 'inactive' | '')}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="label">Search</label>
            <input
              type="text"
              className="input"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="pt-6">
            <span className="text-sm text-gray-500">
              {retailers.length} retailer{retailers.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Retailer List */}
        <div className="lg:col-span-2 space-y-4">
          {selectedIndustry ? (
            // Show flat list when filtered by industry
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {industries.find(i => i.id === selectedIndustry)?.name || selectedIndustry}
              </h2>
              <div className="space-y-2">
                {retailers.map((retailer) => (
                  <RetailerRow
                    key={retailer.retailerId}
                    retailer={retailer}
                    selected={selectedRetailer?.retailerId === retailer.retailerId}
                    onClick={() => setSelectedRetailer(retailer)}
                  />
                ))}
                {retailers.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No retailers found</p>
                )}
              </div>
            </div>
          ) : (
            // Group by industry when showing all
            Object.entries(retailersByIndustry).map(([industry, industryRetailers]) => (
              <div key={industry} className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded ${INDUSTRY_COLORS[industry as RetailerIndustry]}`}>
                    {industries.find(i => i.id === industry)?.name || industry}
                  </span>
                  <span className="text-sm text-gray-500 font-normal">
                    ({industryRetailers.length})
                  </span>
                </h2>
                <div className="space-y-2">
                  {industryRetailers.map((retailer) => (
                    <RetailerRow
                      key={retailer.retailerId}
                      retailer={retailer}
                      selected={selectedRetailer?.retailerId === retailer.retailerId}
                      onClick={() => setSelectedRetailer(retailer)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
          {Object.keys(retailersByIndustry).length === 0 && !selectedIndustry && (
            <div className="card">
              <p className="text-gray-500 text-center py-8">No retailers found. Click "Add Retailer" to create one.</p>
            </div>
          )}
        </div>

        {/* Retailer Details */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
          {selectedRetailer ? (
            <div className="space-y-4">
              <div>
                <label className="label">Retailer ID</label>
                <p className="font-mono text-sm bg-gray-50 p-2 rounded">{selectedRetailer.retailerId}</p>
              </div>
              <div>
                <label className="label">Name</label>
                <p className="font-medium">{selectedRetailer.name}</p>
              </div>
              <div>
                <label className="label">Industry</label>
                <span className={`px-2 py-1 text-xs rounded ${INDUSTRY_COLORS[selectedRetailer.industry]}`}>
                  {industries.find(i => i.id === selectedRetailer.industry)?.name || selectedRetailer.industry}
                </span>
              </div>
              <div>
                <label className="label">Status</label>
                <span className={`px-2 py-1 text-xs rounded ${
                  selectedRetailer.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {selectedRetailer.status}
                </span>
              </div>
              <div>
                <label className="label">Templates
                  <InfoTooltip text="Number of templates in the system for this retailer. Templates are linked via the 'company' field matching this Retailer ID." />
                </label>
                <p>
                  <button
                    className="text-primary-600 hover:text-primary-800 hover:underline"
                    onClick={() => navigate(`/admin/templates?company=${selectedRetailer.retailerId}`)}
                  >
                    View {selectedRetailer.templateCount} template{selectedRetailer.templateCount !== 1 ? 's' : ''} →
                  </button>
                </p>
              </div>
              {selectedRetailer.ediPartnerId && (
                <div>
                  <label className="label">EDI Partner ID</label>
                  <p className="font-mono text-sm">{selectedRetailer.ediPartnerId}</p>
                </div>
              )}
              {selectedRetailer.website && (
                <div>
                  <label className="label">Website</label>
                  <a href={selectedRetailer.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    {selectedRetailer.website}
                  </a>
                </div>
              )}
              {selectedRetailer.contactEmail && (
                <div>
                  <label className="label">Contact Email</label>
                  <a href={`mailto:${selectedRetailer.contactEmail}`} className="text-primary-600 hover:underline">
                    {selectedRetailer.contactEmail}
                  </a>
                </div>
              )}
              {selectedRetailer.notes && (
                <div>
                  <label className="label">Notes</label>
                  <p className="text-sm text-gray-600">{selectedRetailer.notes}</p>
                </div>
              )}
              <div>
                <label className="label">Created</label>
                <p className="text-sm text-gray-600">{new Date(selectedRetailer.createdAt).toLocaleString()}</p>
              </div>
              <div className="pt-4 border-t space-y-2">
                <button
                  className="btn btn-primary w-full"
                  onClick={() => setShowEditModal(true)}
                >
                  Edit Retailer
                </button>
                <div className="relative">
                  <button
                    className="btn btn-secondary w-full text-red-600 hover:text-red-700"
                    onClick={() => {
                      if (confirm(`Are you sure you want to deactivate "${selectedRetailer.name}"?`)) {
                        deleteRetailerMutation.mutate(selectedRetailer.retailerId);
                      }
                    }}
                    disabled={selectedRetailer.status === 'inactive'}
                  >
                    {selectedRetailer.status === 'inactive' ? 'Already Inactive' : 'Deactivate Retailer'}
                  </button>
                  <InfoTooltip text="Deactivating a retailer is a metadata flag only — it does NOT disable templates or prevent label generation." />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Select a retailer to view details</p>
          )}
        </div>
      </div>

      {/* Create Retailer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg m-4">
            <h2 className="text-xl font-bold mb-4">Add Retailer</h2>
            <form onSubmit={handleCreateRetailer} className="space-y-4">
              <div>
                <label className="label">Retailer ID *
                  <InfoTooltip text="Permanent lowercase slug used as the key everywhere. Use kebab-case: walmart, buc-ees, home-depot. Cannot be changed after creation." />
                </label>
                <input
                  name="retailerId"
                  className="input"
                  required
                  pattern="^[a-z0-9-]+$"
                  placeholder="walmart, target, buc-ees"
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and hyphens only</p>
              </div>
              <div>
                <label className="label">Display Name *</label>
                <input
                  name="name"
                  className="input"
                  required
                  placeholder="Walmart, Target, Buc-ee's"
                />
              </div>
              <div>
                <label className="label">Industry *
                  <InfoTooltip text="Determines how the retailer is grouped in lists and how template keys are prefixed." />
                </label>
                <select name="industry" className="input" required>
                  <option value="">Select industry...</option>
                  {industries.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name} - {industry.description}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">EDI Partner ID</label>
                <input
                  name="ediPartnerId"
                  className="input"
                  placeholder="DUNS number or trading partner ID"
                />
              </div>
              <div>
                <label className="label">Website</label>
                <input
                  name="website"
                  type="url"
                  className="input"
                  placeholder="https://www.example.com"
                />
              </div>
              <div>
                <label className="label">Contact Email</label>
                <input
                  name="contactEmail"
                  type="email"
                  className="input"
                  placeholder="edi-support@example.com"
                />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea
                  name="notes"
                  className="input"
                  rows={3}
                  placeholder="Any additional notes about this retailer..."
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={createRetailerMutation.isPending}>
                  {createRetailerMutation.isPending ? 'Creating...' : 'Create Retailer'}
                </button>
              </div>
              {createRetailerMutation.isError && (
                <p className="text-red-600 text-sm">{(createRetailerMutation.error as Error).message}</p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Edit Retailer Modal */}
      {showEditModal && selectedRetailer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg m-4">
            <h2 className="text-xl font-bold mb-4">Edit Retailer</h2>
            <form onSubmit={handleUpdateRetailer} className="space-y-4">
              <div>
                <label className="label">Retailer ID</label>
                <input className="input bg-gray-100" value={selectedRetailer.retailerId} disabled />
              </div>
              <div>
                <label className="label">Display Name *</label>
                <input
                  name="name"
                  className="input"
                  required
                  defaultValue={selectedRetailer.name}
                />
              </div>
              <div>
                <label className="label">Industry *</label>
                <select name="industry" className="input" required defaultValue={selectedRetailer.industry}>
                  {industries.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Status *</label>
                <select name="status" className="input" required defaultValue={selectedRetailer.status}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="label">EDI Partner ID</label>
                <input
                  name="ediPartnerId"
                  className="input"
                  defaultValue={selectedRetailer.ediPartnerId || ''}
                />
              </div>
              <div>
                <label className="label">Website</label>
                <input
                  name="website"
                  type="url"
                  className="input"
                  defaultValue={selectedRetailer.website || ''}
                />
              </div>
              <div>
                <label className="label">Contact Email</label>
                <input
                  name="contactEmail"
                  type="email"
                  className="input"
                  defaultValue={selectedRetailer.contactEmail || ''}
                />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea
                  name="notes"
                  className="input"
                  rows={3}
                  defaultValue={selectedRetailer.notes || ''}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={updateRetailerMutation.isPending}>
                  {updateRetailerMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              {updateRetailerMutation.isError && (
                <p className="text-red-600 text-sm">{(updateRetailerMutation.error as Error).message}</p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Retailer Setup Wizard */}
      {showSetupWizard && (
        <RetailerSetupWizard onClose={() => setShowSetupWizard(false)} />
      )}
    </div>
  );
}

// Retailer row component
function RetailerRow({ retailer, selected, onClick }: { retailer: Retailer; selected: boolean; onClick: () => void }) {
  return (
    <div
      className={`p-3 border rounded cursor-pointer hover:border-primary-300 transition-colors ${
        selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
      }`}
      onClick={onClick}
    >
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-medium">{retailer.name}</h3>
          <p className="text-sm text-gray-500 font-mono">{retailer.retailerId}</p>
        </div>
        <div className="text-right">
          <span className={`px-2 py-1 text-xs rounded ${
            retailer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {retailer.status}
          </span>
          <p className="text-xs text-gray-500 mt-1">
            {retailer.templateCount} template{retailer.templateCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
